import { ReflectUtils } from "@dooboostore/core";
import { ensureInit } from "./elementDefine.js";
import { SwcUtils } from "../utils/Utils.js";
const REQUEST_ANIMATION_FRAME_METADATA_KEY = Symbol.for("simple-web-component:request-animation-frame");
const getActiveEntries = (inst) => inst.__swc_requestAnimationFrameIds ?? (inst.__swc_requestAnimationFrameIds = []);
const startFrameLoop = (win, entries, frameFn) => {
  let currentId = 0;
  const step = (timestamp, prevValue) => {
    const idx = entries.indexOf(currentId);
    if (idx >= 0) entries.splice(idx, 1);
    let result;
    try {
      result = frameFn(timestamp, prevValue);
    } catch (e) {
      console.error("[SWC] requestAnimationFrame callback error:", e);
      return;
    }
    if (result === void 0 || result === null) return;
    currentId = win.requestAnimationFrame((ts) => step(ts, result));
    entries.push(currentId);
  };
  currentId = win.requestAnimationFrame((ts) => step(ts, void 0));
  entries.push(currentId);
  return currentId;
};
const applyRequestAnimationFrame = (options, target, propertyKey, descriptor) => {
  const constructor = target.constructor;
  const normalizedOptions = { ...options, type: options.type ?? "onConnected" };
  let metaList = ReflectUtils.getOwnMetadata(REQUEST_ANIMATION_FRAME_METADATA_KEY, constructor);
  if (!metaList) {
    metaList = [];
    ReflectUtils.defineMetadata(REQUEST_ANIMATION_FRAME_METADATA_KEY, metaList, constructor);
  }
  metaList.push({ propertyKey, options: normalizedOptions });
  if (normalizedOptions.type === "onConnected" || !descriptor) {
    return;
  }
  const original = descriptor.value;
  descriptor.value = function(...args) {
    const res = original.apply(this, args);
    const vk = normalizedOptions.valueKey ?? REQUEST_ANIMATION_FRAME_METADATA_KEY;
    const frameFn = typeof res === "function" ? res : res && typeof res === "object" && typeof res[vk] === "function" ? res[vk] : void 0;
    if (frameFn) {
      ensureInit(this);
      const win = SwcUtils.resolveWindow(this);
      const id = startFrameLoop(win, getActiveEntries(this), frameFn);
      normalizedOptions.created?.(SwcUtils.getHelperAndHostSet(this), id);
    }
    return res;
  };
  return descriptor;
};
function requestAnimationFrame(optionsOrTarget, propertyKey, descriptor) {
  if (propertyKey !== void 0) {
    applyRequestAnimationFrame({}, optionsOrTarget, propertyKey, descriptor);
    return;
  }
  return (target, propertyKey2, descriptor2) => {
    return applyRequestAnimationFrame(optionsOrTarget ?? {}, target, propertyKey2, descriptor2);
  };
}
const findAllRequestAnimationFrameMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return (ReflectUtils.findAllMetadata(REQUEST_ANIMATION_FRAME_METADATA_KEY, constructor) || []).flat();
};
class RequestAnimationFrameLifeCycler {
  onConnected(helperHostSet) {
    const inst = helperHostSet.$this;
    inst.__swc_requestAnimationFrameIds = [];
    const entries = getActiveEntries(inst);
    for (const meta of findAllRequestAnimationFrameMetadata(inst)) {
      if (meta.options.type !== "onConnected") continue;
      const frameFn = (timestamp, prevValue) => inst[meta.propertyKey](timestamp, prevValue);
      const id = startFrameLoop(helperHostSet.$w, entries, frameFn);
      meta.options.created?.(helperHostSet, id);
    }
  }
  onDisconnected(helperHostSet) {
    const inst = helperHostSet.$this;
    for (const id of inst.__swc_requestAnimationFrameIds ?? []) {
      try {
        helperHostSet.$w.cancelAnimationFrame(id);
      } catch (e) {
        console.error("[SWC] cancelAnimationFrame error:", e);
      }
    }
    inst.__swc_requestAnimationFrameIds = [];
  }
}
function requestAnimationFrameReturnValue(optionsOrTarget, propertyKey, descriptor) {
  if (propertyKey !== void 0) {
    applyRequestAnimationFrame({ type: "returnValue" }, optionsOrTarget, propertyKey, descriptor);
    return;
  }
  return (target, propertyKey2, descriptor2) => applyRequestAnimationFrame({ ...optionsOrTarget ?? {}, type: "returnValue" }, target, propertyKey2, descriptor2);
}
export {
  REQUEST_ANIMATION_FRAME_METADATA_KEY,
  RequestAnimationFrameLifeCycler,
  findAllRequestAnimationFrameMetadata,
  requestAnimationFrame,
  requestAnimationFrameReturnValue
};
//# sourceMappingURL=requestAnimationFrame.js.map
